import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "./store";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

// ─── Types ───────────────────────────────────────────────
export interface CardInfo {
  paymentMethodId: string;
  fingerprint: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding: string;
  country: string;
  isDefault: boolean;
  isActive: boolean;
  addedAt?: string;
}

interface PaymentMethodsState {
  cards: CardInfo[];
  defaultCard?: string;
  loading: boolean;
  lastUpdated?: string;
}

const initialState: PaymentMethodsState = {
  cards: [],
  defaultCard: undefined,
  loading: false,
  lastUpdated: undefined,
};

// ─── Slice ───────────────────────────────────────────────
const paymentMethodsSlice = createSlice({
  name: "paymentMethods",
  initialState,
  reducers: {
    setPaymentMethodData: (
      state,
      action: PayloadAction<Partial<PaymentMethodsState>>
    ) => {
      return { ...state, ...action.payload, loading: false };
    },
    setPaymentMethodLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    resetPaymentMethodData: () => initialState,
  },
});

export const {
  setPaymentMethodData,
  setPaymentMethodLoading,
  resetPaymentMethodData,
} = paymentMethodsSlice.actions;

export default paymentMethodsSlice.reducer;

//
// ─── THUNK: Listen to Firestore for updates ───────────────────────────────
//

// 🔹 Listen to user's saved cards in `paymentMethods/{userId}`
export const listenToPaymentMethods =
  (userId: string) => (dispatch: AppDispatch) => {
    if (!userId) return;
    dispatch(setPaymentMethodLoading(true));

    const ref = doc(db, "paymentMethods", userId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();

          const cards =
            (data.cards || []).map((c: any) => ({
              paymentMethodId: c.paymentMethodId,
              fingerprint: c.fingerprint,
              brand: c.brand,
              last4: c.last4,
              expMonth: c.expMonth,
              expYear: c.expYear,
              funding: c.funding,
              country: c.country,
              isDefault: c.isDefault ?? false,
              isActive: c.isActive ?? true,
              addedAt: c.addedAt?.toDate
                ? c.addedAt.toDate().toISOString()
                : undefined,
            })) || [];

          dispatch(
            setPaymentMethodData({
              cards,
              defaultCard: data.defaultCard,
              lastUpdated: data.updatedAt?.toDate
                ? data.updatedAt.toDate().toISOString()
                : undefined,
            })
          );

          console.log("✅ Fetched payment methods:", cards);
        } else {
          console.warn("⚠️ No paymentMethods doc found for user:", userId);
          dispatch(resetPaymentMethodData());
        }
      },
      (error) => {
        console.error("❌ Firestore listener error:", error);
        dispatch(resetPaymentMethodData());
      }
    );

    // ✅ Return unsubscribe for cleanup
    return unsub;
  };
